<script setup>
// Second step for codes submitted without their trailing part: the Wowcher family and
// Virgin Experience Days both print the code and PIN separately. Presentational only -
// it collects the PIN and hands it back to the flow that opened it, which decides what
// happens next (a PIN can still be followed by the provider picker).
//
// Shared by both journeys that resolve a voucher code: the header's Redeem bar and the
// check-balance page. Which one opened it arrives as `flow`, so this panel submits back into
// the right instance without knowing which endpoint is behind it.
import { computed, ref } from 'vue';

const props = defineProps({
    code: { type: String, required: true },
    // Set only when the customer has already told us who issued the voucher, which happens
    // when the picker came first. Null on the common path.
    provider: { type: Object, default: null },
    // The createVoucherFlow() instance driving this journey - see shared/voucherFlow.js.
    flow: { type: Object, required: true },
});

const state = props.flow.state;

const pin = ref('');

// Built here rather than with an inline <template v-if>, which leaves a space before the
// full stop when there is no provider to name.
const intro = computed(() => (props.provider
    ? `Your voucher number needs a second part before we can check it with ${props.provider.name}.`
    : 'Your voucher number needs a second part before we can check it.'));

function submit() {
    const value = pin.value.trim();

    if (! value) {
        return;
    }

    props.flow.submit({ code: props.code, pin: value, provider: props.provider?.slug });
}
</script>

<template>
    <div class="redeem_panel">
        <p class="redeem_panel_intro">
            {{ intro }}
            You will find it printed alongside <strong>{{ code }}</strong> on your voucher.
        </p>

        <form class="redeem_panel_form" @submit.prevent="submit">
            <label for="voucher_pin">PIN or security code</label>
            <input
                id="voucher_pin"
                v-model="pin"
                type="text"
                name="voucher_pin"
                autocomplete="off"
                autocapitalize="characters"
                spellcheck="false"
                autofocus
            >

            <p v-if="state.error" class="redeem_panel_error">{{ state.error }}</p>

            <button type="submit" class="button teal" :disabled="state.busy">
                {{ state.busy ? 'Checking...' : 'Continue' }}
            </button>
        </form>
    </div>
</template>
